// react and external-libs
import React, { useCallback, useEffect, useMemo, useState } from 'react';
// redux
import { useAppSelector } from '../../../../app/hooks';
// interface
import { Survey } from '../../../../interfaces/survey.interface';
import { AssessmentSurvey, AssessmentNotification } from '../../../../interfaces/assessment.interface';
import User, { Company, CompanyApplication, Contact, Badge } from '../../../../interfaces/users.interface';
// services
import { fetchSurvey } from '../../../../services/surveys';
import { getHighestAchievement, fetchGoldBadgeLabel, fetchBlueBadgeLabel, getBadgeRank, BLUE_BADGE_LABEL, GOLD_BADGE_LABEL, SILVER_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL, selectBadgeFromArray } from '../../../../services/badge';
import { fetchAssessmentSurvey } from '../../../../services/assessments';
import { wfHierarchy } from '../../../../services/workflow';
// components
import SiteName from './SiteName';
import GetBadge from '../../GetBadge';
import WorkflowStatus from '../../WorkflowStatus';
import WorkflowButtons from '../../WorkflowButtons';
import { DataCell, DataRow } from '../../../Tables/DataList';
import ChipsWithOverflow from '../../../Layout/ChipsWithOverflow';
import AssessmentNotificationsAlertIcon from '../../AssessmentNotificationsAlertIcon';
import { useTheme } from '@mui/material/styles';
import AnimatedSidebarIcon from '../../AnimatedSidebarIcon';

type SiteDataRowProps = {
  company: Company;
  site: Contact;
  userCanAdminCompany: boolean;
  companySuccessCallback: any;
  showCanBeginNewWorkflow?: boolean;
  setCanStartNewQuestionniare: (canStartNewQuestionniare: boolean) => void;
  onClick: (site: Contact) => void;
};

const SiteDataRow = (props: SiteDataRowProps) => {
  // props
  const { company, site, onClick, setCanStartNewQuestionniare, showCanBeginNewWorkflow } = props;
  // redux
  const { workflows: companyWorkflows } = useAppSelector((state) => state.workflow);
  // states
  const [cSite, setcSite] = useState(props.site);
  const [curSurvey, setCurSurvey] = useState<Survey | undefined>();
  const [assessmentSurvey, setAssessmentSurvey] = useState<AssessmentSurvey | undefined>();
  // local variables
  const workflows = useMemo(() => {
    return [...(companyWorkflows?.filter((wf) => wf.site === site.id) || [])]
      .sort((a, b) => {
        const badgeRankA = getBadgeRank(a.badges as Badge[]);
        const badgeRankB = getBadgeRank(b.badges as Badge[]);

        if (badgeRankA !== badgeRankB) return badgeRankB - badgeRankA;

        // sort by wf hierarchy if non-expired badge tie
        const statusDiff = wfHierarchy.indexOf(b.status as typeof wfHierarchy[number]) - wfHierarchy.indexOf(a.status as typeof wfHierarchy[number]);
        if (statusDiff !== 0) return statusDiff;

        return b.id - a.id;
      });
  }, [companyWorkflows, site.id, wfHierarchy]);

  const workflow = useMemo(() => (workflows && workflows.length ? workflows[0] : undefined), [workflows]);
  const highestAchievementBlue = useMemo(() => getHighestAchievement(workflows, [BLUE_BADGE_LABEL]), [workflows]);
  const highestAchievementGold = useMemo(() => getHighestAchievement(
    workflows,
    [
      GOLD_BADGE_LABEL,
      SILVER_BADGE_LABEL,
      GOLD_NEXT_GEN_BADGE_LABEL,
      GOLD_STAR_BADGE_LABEL,
    ],
  ), [workflows]);
  // The covering certification + app names are embedded on the site by the
  // company-detail endpoint (newest cert first), so no company-wide lookup.
  const companyCertification = useMemo(() => cSite?.certifications?.[0], [cSite?.certifications]);
  const serviceNames = useMemo(() => cSite?.services?.map((service) => service.name), [cSite?.services]);
  const applicationNames = useMemo(
    () => cSite?.companyApplications?.map((ca: CompanyApplication) => ca.application?.name ?? 'Name Missing'),
    [cSite?.companyApplications],
  );

  const handleFetchAssessmentSurvey = useCallback(async (assessmentSurveyId: number) => {
    const assSurvey = await fetchAssessmentSurvey(assessmentSurveyId);
    if (workflow?.status !== 'Complete' ||
      (workflow?.status === 'Complete' && assSurvey.bestPracticeRemediationCount)) {
      setAssessmentSurvey(assSurvey);
    }
  }, []);

  const handleFetchSurvey = useCallback(async (surveyId: number) => {
    const surveyData = await fetchSurvey(surveyId);
    setCurSurvey(surveyData);
  }, []);

  // loads the survey?
  useEffect(() => {
    if (workflow?.assessmentSurvey) {
      // fetch assessment survey
      handleFetchAssessmentSurvey(workflow?.assessmentSurvey);
      return;
    }

    // we have a bp survey and no assessment
    if (
      workflow?.bpSurvey &&
      !workflow?.assessment &&
      !['submittedForAssessment', 'complete'].includes(workflow?.status)
    ) {
      // fetch survey
      handleFetchSurvey(workflow?.bpSurvey);
      return;
    }
  }, [workflow]);

  // setting of site from props
  useEffect(() => {
    setcSite(site);
  }, [site]);

  const blueBadgeLabel = useMemo(() => highestAchievementBlue?.badge && fetchBlueBadgeLabel([highestAchievementBlue.badge], highestAchievementBlue.version) || '', [highestAchievementBlue]);
  const goldBadgeLabel = useMemo(() => highestAchievementGold?.badge && fetchGoldBadgeLabel([highestAchievementGold.badge], highestAchievementGold.version) || '', [highestAchievementGold]);
  const [assessmentNotifications, setAssessmentNotifications] = useState<AssessmentNotification[]>([]);

  const handleSetCanStartNewQuestionniare = useCallback(() => {
    setCanStartNewQuestionniare(true);
    onClick(site);
  }, [setCanStartNewQuestionniare, onClick, site]);

  const theme = useTheme();

  return (
    <>
      <DataRow onClick={() => onClick(site)} hover
        sx={{
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            '& .slide-icon': {
              fill: theme.palette.common.white, 
              color: theme.palette.common.white, // ← Use color, not fill
            },
            '& .hoverBox': {
              // Grey background
              backgroundColor: theme.palette.primary.main,
              transform: 'translateX(-4px)', 
            },
          },
        }}>
        <DataCell xs={1} textAlign='center'>
          <GetBadge
            shield={workflow && selectBadgeFromArray(workflow.badges as Badge[], true)}
            isActive={company.isPublished}
            goldBadgeLabel={highestAchievementGold?.badge && fetchGoldBadgeLabel([highestAchievementGold.badge], highestAchievementGold.version, highestAchievementGold.badge.title)}
            blueBadgeLabel={blueBadgeLabel}
            assessmentSurvey={assessmentSurvey}
            assessor={workflow?.assignedAssessor as User | undefined}
            certification={companyCertification}
            company={company.id}
          />
        </DataCell>
        <DataCell xs={3}>
          <SiteName contact={cSite} />
        </DataCell>
        <DataCell
          xs={2}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.25rem',
            padding: '0.25rem',
          }}
        >
          {ChipsWithOverflow(applicationNames, 1, 'app')}
        </DataCell>
        <DataCell
          xs={2}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.25rem',
            padding: '0.25rem',
          }}
        >
          {ChipsWithOverflow(serviceNames, 1, 'service')}
        </DataCell>
        <DataCell xs={1}>
          <>
            <AssessmentNotificationsAlertIcon assessmentNotifications={assessmentNotifications} />
            <WorkflowStatus
              hasGoldExpired={goldBadgeLabel.includes('Expired')}
              hasBlueExpired={blueBadgeLabel.includes('Expired')}
              workflow={workflow}
              company={company}
              survey={curSurvey}
              assessmentSurvey={assessmentSurvey}
            />
          </>
        </DataCell>
        <DataCell xs={2} textAlign='center'>
          <WorkflowButtons
            company={company}
            workflow={workflow}
            survey={curSurvey}
            assessmentSurvey={assessmentSurvey}
            site={cSite}
            showCanBeginNewWorkflow={showCanBeginNewWorkflow}
            setAssessmentNotifications={setAssessmentNotifications}
            setCanStartNewQuestionniare={handleSetCanStartNewQuestionniare}
          />
        </DataCell>
        <DataCell xs={1} sx={{ display: 'flex', justifyContent: 'center', pr: 2 }}>
          <AnimatedSidebarIcon />
        </DataCell>
      </DataRow>
    </>
  );
};

export default SiteDataRow;
